/* Colonna / Pipeline Quick View subscriptions. No network or formdata hooks. */
(() => {
  'use strict';
  if (window.ColonnaQuickviewSubscriptions) return;
  const selectorQuery = '[data-n2qv-selector]';
  const states = new WeakMap();
  let sequence = 0;

  function interval(name) {
    const match = String(name).match(/\b(?:every\s+)?(\d+)\s*(day|week|month|year)s?\b/i);
    if (!match) return { key: String(name).toLowerCase(), label: String(name) };
    const count = Number(match[1]);
    const unit = match[2].toLowerCase();
    return { key: `${count}:${unit}`, label: `Every ${count} ${unit}${count === 1 ? '' : 's'}` };
  }

  function initialise(selector) {
    if (!selector.isConnected) return null;
    const wrapper = selector.closest('[data-product-form-wrapper]');
    const form = wrapper?.querySelector('form[data-product-form]');
    // The theme may insert the selector before inserting the form.
    if (!wrapper || !form) return null;
    const previous = states.get(selector);
    if (previous?.form === form) return previous;
    previous?.destroy();

    const status = selector.querySelector('[data-n2qv-status]');
    const fieldset = selector.querySelector('[data-n2qv-fieldset]');
    const planSelect = selector.querySelector('[data-n2qv-plan]');
    const frequency = selector.querySelector('[data-n2qv-frequency]');
    const otp = selector.querySelector('[data-n2qv-purchase][value="onetime"]');
    const sub = selector.querySelector('[data-n2qv-purchase][value="subscription"]');
    const otpPrice = selector.querySelector('[data-n2qv-onetime-price]');
    const subPrice = selector.querySelector('[data-n2qv-subscription-price]');
    const idInput = form.querySelector('[name="id"]');
    let config;
    try {
      config = JSON.parse(selector.querySelector('[data-n2qv-config]').textContent);
      if (!Array.isArray(config.variants) || !Array.isArray(config.optionNames)) throw new Error('Invalid config');
      if (!fieldset || !planSelect || !frequency || !sub || !subPrice || !idInput) throw new Error('Incomplete form');
    } catch (_) {
      if (status) {
        status.textContent = 'Please open the full product page to choose your purchase options.';
        status.hidden = false;
      }
      return null;
    }

    // Cached/cloned popups must not share radio names or label targets.
    const uid = `n2qv-instance-${++sequence}`;
    selector.querySelectorAll('[data-n2qv-purchase]').forEach(radio => { radio.name = `${uid}-purchase`; });
    planSelect.id = `${uid}-plan`;
    selector.querySelector('[data-n2qv-plan-label]')?.setAttribute('for', planSelect.id);
    if (config.subscriptionOnly || !config.oneTimeAllowed) sub.checked = true;

    let sourceId = null;
    let lastPlanKey = null;
    let timer = null;
    let valid = false;
    let cartId = null;
    let cartPlan = null;
    const listeners = new AbortController();

    function message(text) {
      if (!status) return;
      status.textContent = text;
      status.hidden = !text;
    }

    function setValid(value) {
      valid = value;
      Array.from(form.elements).filter(el => el.matches('[data-add-to-cart]')).forEach(button => {
        button.disabled = !valid;
        button.setAttribute('aria-disabled', String(!valid));
      });
    }

    function selectedVariant() {
      const values = new Map();
      wrapper.querySelectorAll('input[name], select[name]').forEach(control => {
        if (!control.name.startsWith('options[')) return;
        if ((control.type === 'radio' || control.type === 'checkbox') && control.checked) {
          values.set(control.name, control.value);
        } else if ((control.tagName === 'SELECT' || control.type === 'hidden') && control.value && !values.has(control.name)) {
          values.set(control.name, control.value);
        }
      });
      const chosen = config.optionNames.map((name, index) => {
        const value = values.get(`options[${name}]`);
        if (value !== undefined) return value;
        const possible = [...new Set(config.variants.map(variant => variant.options[index]))];
        return possible.length === 1 ? possible[0] : undefined;
      });
      const match = config.variants.find(variant => variant.options.every((value, index) => value === chosen[index]));
      if (match) return match;
      // A partially chosen or invalid combination must not buy a stale variant.
      if (values.size) return null;
      return config.variants.find(variant => String(variant.sourceId) === String(idInput.value) || String(variant.targetId) === String(idInput.value)) || null;
    }

    function quantity() {
      const control = form.elements.namedItem('quantity') || wrapper.querySelector('[name="quantity"]');
      const value = Number.parseInt(control?.value || '1', 10);
      return Number.isFinite(value) && value > 0 ? value : 1;
    }

    function money(cents, formatted, count) {
      if (count === 1 && formatted) return formatted;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
          style: 'currency', currency: config.currency
        }).format(Number(cents) * count / 100);
      } catch (_) {
        return `${config.currency} ${(Number(cents) * count / 100).toFixed(2)}`;
      }
    }

    function renderPlans(variant) {
      const selected = planSelect.selectedOptions[0];
      if (selected) lastPlanKey = selected.dataset.intervalKey;
      planSelect.replaceChildren();
      variant.plans.forEach(plan => {
        const option = document.createElement('option');
        const parsed = interval(plan.name);
        option.value = String(plan.id);
        option.textContent = parsed.label;
        option.dataset.intervalKey = parsed.key;
        planSelect.appendChild(option);
      });
      const options = Array.from(planSelect.options);
      const matching = options.find(option => option.dataset.intervalKey === lastPlanKey) || options[0];
      if (matching) {
        matching.selected = true;
        lastPlanKey = matching.dataset.intervalKey;
      }
    }

    function writeCartFields() {
      if (!valid) return;
      idInput.value = String(cartId);
      let planInput = form.querySelector('[data-n2qv-selling-plan]');
      if (cartPlan) {
        if (!planInput) {
          planInput = document.createElement('input');
          planInput.type = 'hidden';
          planInput.name = 'selling_plan';
          planInput.dataset.n2qvSellingPlan = '';
          form.appendChild(planInput);
        }
        planInput.value = String(cartPlan);
        planInput.disabled = false;
      } else {
        planInput?.remove();
      }
    }

    function sync(write = false) {
      if (!selector.isConnected || !form.isConnected) return false;
      const variant = selectedVariant();
      if (!variant) {
        setValid(false);
        message('Please choose an available roast and weight.');
        return false;
      }
      if (sourceId !== String(variant.sourceId)) {
        renderPlans(variant);
        sourceId = String(variant.sourceId);
      }
      const plan = variant.plans.find(item => String(item.id) === String(planSelect.value));
      const otpAvailable = Boolean(config.oneTimeAllowed && variant.sourceAvailable);
      const subAvailable = Boolean(variant.targetId && variant.targetAvailable && plan);
      if (otp) otp.disabled = !otpAvailable;
      sub.disabled = !subAvailable;
      const isSub = sub.checked;
      const count = quantity();
      if (otpPrice) otpPrice.textContent = money(variant.oneTimePriceCents, variant.oneTimePrice, count);
      subPrice.textContent = subAvailable ? money(plan.priceCents, plan.price, count) : 'Unavailable';
      frequency.hidden = !isSub || !subAvailable;
      selector.querySelectorAll('[data-n2qv-card]').forEach(card => {
        const radio = card.querySelector('[data-n2qv-purchase]');
        card.classList.toggle('is-active', radio.checked);
        card.classList.toggle('is-disabled', radio.disabled);
      });
      // Never silently replace an unavailable subscription with a one-time order.
      cartId = isSub ? variant.targetId : variant.sourceId;
      cartPlan = isSub ? plan?.id : null;
      setValid(isSub ? subAvailable : otpAvailable);
      if (write) writeCartFields();
      wrapper.querySelectorAll('.product__submit__quick, .shopify-payment-button').forEach(el => {
        el.hidden = isSub || !valid;
      });
      if (!valid) {
        message(isSub ? 'Subscription is unavailable for this option. Choose another option or a one-time purchase.' : 'One-time purchase is unavailable for this option.');
      } else if (!subAvailable && !config.subscriptionOnly) {
        message('Subscription is unavailable for this option.');
      } else {
        message('');
      }
      return valid;
    }

    function handleChange(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (selector.contains(target) && target.matches('[data-n2qv-purchase], [data-n2qv-plan]')) {
        sync(true);
      } else if (target.name?.startsWith('options[') || target.name === 'quantity') {
        window.clearTimeout(timer);
        // Let Pipeline finish its variant update before writing the mapped ID.
        timer = window.setTimeout(() => sync(true), 0);
      }
    }

    const state = {
      form, sync,
      destroy() { window.clearTimeout(timer); listeners.abort(); states.delete(selector); }
    };
    states.set(selector, state);
    // Capture also receives Pipeline's non-bubbling quantity events.
    wrapper.addEventListener('change', handleChange, { capture: true, signal: listeners.signal });
    wrapper.addEventListener('input', handleChange, { capture: true, signal: listeners.signal });
    fieldset.disabled = false;
    sync(false);
    const submitArea = wrapper.querySelector('[data-n2qv-submit]');
    if (submitArea) submitArea.hidden = false;
    const fallback = selector.querySelector('[data-n2qv-fallback]');
    if (fallback) fallback.hidden = true;
    return state;
  }

  function eachSelector(root, callback) {
    if (root.nodeType !== 1 && root.nodeType !== 9) return;
    if (root.matches?.(selectorQuery)) callback(root);
    root.querySelectorAll(selectorQuery).forEach(callback);
  }

  function scan(root) {
    eachSelector(root, initialise);
    const wrapper = root.closest?.('[data-product-form-wrapper]');
    if (wrapper) eachSelector(wrapper, initialise);
  }

  function stateForForm(form) {
    if (!(form instanceof HTMLFormElement)) return null;
    const selector = form.closest('[data-product-form-wrapper]')?.querySelector(selectorQuery);
    if (!selector) return null;
    const state = initialise(selector);
    return state?.form === form ? state : null;
  }

  function guard(event, form) {
    const state = stateForForm(form);
    if (!state) return;
    if (!state.sync(true)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-add-to-cart], button[type="submit"], input[type="submit"]');
    if (button) guard(event, button.form);
  }, true);
  document.addEventListener('submit', event => guard(event, event.target), true);

  function start() {
    scan(document);
    const observer = new MutationObserver(records => {
      records.forEach(record => {
        record.removedNodes.forEach(node => eachSelector(node, selector => {
          if (!selector.isConnected) states.get(selector)?.destroy();
        }));
        record.addedNodes.forEach(node => {
          // Price text and dropdown changes are not discovery events.
          if (node.nodeType === 1 && !node.closest(selectorQuery)) scan(node);
          else if (node.nodeType === 1 && node.matches(selectorQuery)) scan(node);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
  window.ColonnaQuickviewSubscriptions = { scan, version: '2026-09-17' };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
